import type { Defineable } from "@internal/backstage-plugin-utility-common";

import { gitUrlParse } from "../gitUrlParse";

/**
 * The canonical Backstage annotation used to record a catalog entity's
 * GitHub repository as an "owner/repo" slug.
 *
 * @public
 */
export const GITHUB_PROJECT_SLUG_ANNOTATION = "github.com/project-slug";

/**
 * Extracts a repository slug from a GitHub source location string.
 *
 * Built on {@link gitUrlParse}, which requires an actual URL — Backstage's
 * `backstage.io/source-location` annotation is always a `url:<url>`-prefixed
 * URL in practice, so the `url:` prefix is stripped before parsing. Returns
 * `undefined` for anything that doesn't parse as a URL, or whose host isn't
 * `github.com` (e.g. a GitLab source location).
 *
 * @example
 * extractRepoFromSourceLocation("url:https://github.com/Banno/my-service/blob/main/catalog-info.yaml")
 * // => "Banno/my-service"
 *
 * @public
 */
export function extractRepoFromSourceLocation(
    sourceLocation: Defineable<string>
): Defineable<string> {
    if (!sourceLocation) return undefined;
    try {
        const parsed = gitUrlParse(sourceLocation.replace(/^url:/, ""));
        if (parsed.source !== "github.com") return undefined;
        return `${parsed.owner}/${parsed.name}`;
    } catch {
        return undefined;
    }
}

/**
 * Resolves the GitHub "owner/repo" slug for a catalog entity from its
 * annotations.
 *
 * Checks, in order:
 * 1. `github.com/project-slug` (the canonical Backstage annotation)
 * 2. `backstage.io/source-location` (a GitHub URL)
 *
 * @param annotations - The entity's metadata annotations
 * @returns The "owner/repo" slug, or null if none could be resolved
 *
 * @public
 */
export function resolveGithubSlug(
    annotations: Record<string, string>
): string | null {
    const projectSlug = annotations[GITHUB_PROJECT_SLUG_ANNOTATION];
    if (projectSlug && projectSlug.includes("/")) {
        return projectSlug.replace(/\.git$/, "");
    }

    const sourceLocation = extractRepoFromSourceLocation(
        annotations["backstage.io/source-location"]
    );
    if (sourceLocation) {
        return sourceLocation.replace(/\.git$/, "");
    }

    return null;
}

/**
 * Splits an "owner/repo" GitHub slug into its parts.
 *
 * Uses `indexOf` rather than `split` so that repo names containing slashes
 * (e.g. GitHub Enterprise paths like "org/team/repo") are preserved intact.
 *
 * Deliberately NOT built on {@link gitUrlParse}: `slug` is a bare
 * "owner/repo" string, not a URL, and `gitUrlParse` throws on exactly the
 * multi-segment Enterprise-path input this function is designed to handle
 * (e.g. `gitUrlParse("Banno/group/repo")` fails to parse) — using it here
 * would be a regression, not a simplification.
 *
 * @param slug - The GitHub slug (e.g. "Banno/backstage")
 * @returns `{ owner, repo }`, or null if the slug is malformed
 *
 * @public
 */
export function parseGithubSlug(
    slug: string
): { owner: string; repo: string } | null {
    const slashIdx = slug.indexOf("/");
    if (slashIdx <= 0 || slashIdx === slug.length - 1) {
        return null;
    }
    return { owner: slug.slice(0, slashIdx), repo: slug.slice(slashIdx + 1) };
}
